import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { Bell, BellRinging, CheckCircle, Trash, ThermometerHot, Drop, Sun, CloudRain, Wind } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const ICONS = {
  heat_stress: ThermometerHot,
  cold_stress: ThermometerHot,
  low_humidity: Drop,
  high_humidity: Drop,
  uv_overexposure: Sun,
  water_logging_risk: CloudRain,
  wind_stress: Wind,
};

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {
      toast.error("Couldn't load notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const markRead = async () => {
    try {
      await api.post("/notifications/mark_read");
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch {}
  };

  const remove = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setItems((arr) => arr.filter((n) => n.notif_id !== id));
    } catch {}
  };

  return (
    <div className="min-h-screen bg-sand-50" data-testid="notifications-page">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono-data text-mute">your alerts</div>
            <h1 className="font-display text-4xl md:text-5xl text-ink leading-tight">Notifications</h1>
            <p className="text-mute mt-1"><span data-testid="unread-count">{unread}</span> unread</p>
          </div>
          {items.length > 0 && (
            <button onClick={markRead} className="btn-secondary !text-sm" data-testid="mark-all-read">
              <CheckCircle size={14} /> Mark all read
            </button>
          )}
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="font-mono-data text-mute animate-pulse-soft">loading…</div>
          ) : items.length === 0 ? (
            <div className="bg-sand-100 border border-sand-200 rounded-3xl p-12 text-center" data-testid="empty-notifications">
              <Bell size={48} weight="duotone" className="text-forest mx-auto mb-4" />
              <h3 className="font-display text-3xl">No alerts yet</h3>
              <p className="text-mute mt-2">When your local climate crosses a plant's threshold, you'll see it here.</p>
              <Link to="/plants" className="btn-primary mt-6 inline-flex"><Bell size={14} /> Manage plants</Link>
            </div>
          ) : (
            <div className="space-y-3" data-testid="notifications-list">
              {items.map((n) => (
                <NotifCard key={n.notif_id} n={n} onRemove={() => remove(n.notif_id)} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function NotifCard({ n, onRemove }) {
  const sev = n.severity || 0;
  const sevClass = sev > 70 ? "severity-severe" : sev > 40 ? "severity-moderate" : sev > 0 ? "severity-mild" : "severity-none";
  return (
    <div className={`flex gap-4 p-4 rounded-2xl border ${n.read ? "bg-sand-100 border-sand-200" : "bg-white border-forest/20 shadow-soft"}`} data-testid="notif-card">
      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${n.read ? "bg-sand-200 text-mute" : "bg-forest text-white"}`}>
        {n.kind === "billing" ? <CheckCircle size={22} weight="duotone" /> : <BellRinging size={22} weight="duotone" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display text-xl text-ink leading-tight">{n.title}</h3>
          <button onClick={onRemove} className="text-mute hover:text-terra shrink-0" data-testid="notif-delete">
            <Trash size={14} />
          </button>
        </div>
        <p className="text-mute text-sm mt-1">{n.body}</p>
        {Array.isArray(n.triggers) && n.triggers.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {n.triggers.map((t, i) => {
              const I = ICONS[t.type] || ThermometerHot;
              return (
                <span key={i} className={`tag ${sevClass} inline-flex items-center gap-1`}>
                  <I size={11} weight="duotone" /> {t.type.replace(/_/g, " ")} · {t.value}{t.limit != null && <> / {t.limit}</>}
                </span>
              );
            })}
          </div>
        )}
        <div className="font-mono-data text-mute text-xs mt-3 flex items-center gap-3">
          <span>{new Date(n.created_at).toLocaleString()}</span>
          {sev > 0 && <span>severity {sev}</span>}
          {n.location_label && <span>· {n.location_label}</span>}
        </div>
      </div>
    </div>
  );
}
